import { inject } from '@angular/core';
import { DevCycleService } from '@services/dev-cycle.service';
import { Observable, from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export interface FeatureFlagConfig {
  name: string;
  defaultValue?: boolean;
  description?: string;
}

/**
 * Helper functions para feature flags que podem ser usadas em qualquer componente
 */

// Função helper para obter uma única flag
export const getFeatureFlag = (
  flagName: string,
  defaultValue: boolean = false,
): Observable<boolean> => {
  const devCycleService = inject(DevCycleService);

  return from(devCycleService.getVariable(flagName, defaultValue)).pipe(
    map((value) => Boolean(value)),
    shareReplay(1),
  );
};

// Função helper para obter múltiplas flags
export const getFeatureFlags = (
  ...configs: FeatureFlagConfig[]
): Observable<Record<string, boolean>> => {
  const devCycleService = inject(DevCycleService);

  const promises = configs.map(async (config) => ({
    name: config.name,
    value: await devCycleService.getVariable(
      config.name,
      config.defaultValue ?? false,
    ),
  }));

  return from(Promise.all(promises)).pipe(
    map((results) =>
      results.reduce((acc, { name, value }) => {
        acc[kebabToCamelCase(name)] = Boolean(value);
        return acc;
      }, {} as Record<string, boolean>),
    ),
    shareReplay(1),
  );
};

// Função helper para criar um observable de feature flag com transformação de nome
export const createFeatureFlagObservable = (
  flagName: string,
  defaultValue: boolean = false,
): Observable<{ name: string; value: boolean }> => {
  const devCycleService = inject(DevCycleService);

  return from(devCycleService.getVariable(flagName, defaultValue)).pipe(
    map((value) => ({
      name: kebabToCamelCase(flagName),
      value: Boolean(value),
    })),
    shareReplay(1),
  );
};

// Utility function
const kebabToCamelCase = (str: string): string =>
  str
    .split('-')
    .map((word, index) =>
      index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join('');
